import React from 'react';
import { connect } from 'react-redux';
import { Box, Divider, TextLink, Typography } from '@drone-js/core';
import AgodaMarkdown from 'component/common/AgodaMarkdown/AgodaMarkdown';
import OverviewDescription from 'component/page/detail/DetailCard/Overview/OverviewDescription';
import styled from 'styled-components';
import { Breakpoint } from 'store/slice/screen';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import { ActivitiesReadMoreButtonClickEventActivitiesFieldsV1 } from '@agoda/analytics-messages-definition-activities/events.split';
import { ActivitiesReadMoreButtonClickEventActivitiesManifestV1 } from '@agoda/analytics-messages-definition-activities/manifest/events';
import { ActivityOverviewProps, mapStateToProps } from 'component/page/detail/DetailCard/Overview/ActivityOverview.selectors';

export const OVERVIEW_ELEMENT_NAME = 'activities-overview';

const height = {
    xl: 80,
    l: 100,
    m: 120,
    s: 140,
};

const getMaxHeight = (breakpoint: Breakpoint, isDisplayFullContent?: boolean) => {
    if (isDisplayFullContent) {
        return Infinity;
    }
    if (breakpoint.xl) {
        return height.xl;
    }
    if (breakpoint.l) {
        return height.l;
    }
    if (breakpoint.m) {
        return height.m;
    }
    return height.s;
};

const BlurLine = styled.div<{ height: string; blur: string }>`
    height: ${(props) => props.height};
    mask-image: ${(props) => props.blur};
`;
const ActivityDescription: React.FC<React.PropsWithChildren<{ contentLocale: string; activityDescription: string; disableLinkFocus?: boolean }>> = (props) => {
    return (
        <Typography variant="body-default" lang={props.contentLocale} textColor="base.default" tag="span">
            <AgodaMarkdown dataElementName="overview-description" input={props.activityDescription} isActivityDescription={true} disableLinkFocus={props.disableLinkFocus} />
        </Typography>
    );
};
ActivityDescription.displayName = 'ActivityDescription';

const ActivityOverview: React.FC<ActivityOverviewProps> = (props) => {
    const { cms, activity, contentLocale, breakpoint, isBotOrIsPrerender, isDisplayFullContent } = props;
    const ref = React.useRef<HTMLDivElement>(null);
    const [isOverviewDescriptionVisible, setOverviewDescriptionVisible] = React.useState(false);
    const [currentHeight, setCurrentHeight] = React.useState(0);
    const isExceedHeight = currentHeight > getMaxHeight(breakpoint, isDisplayFullContent);
    const maxHeight = getMaxHeight(breakpoint, isDisplayFullContent);

    React.useEffect(() => {
        if (ref.current) {
            setCurrentHeight(ref.current.clientHeight);
        }
    }, [activity.description]);

    if (!activity || !activity.description || activity.description.length === 0) {
        return null;
    }

    const onReadMoreClick = () => {
        trackElementEvent<ActivitiesReadMoreButtonClickEventActivitiesFieldsV1>(ActivitiesReadMoreButtonClickEventActivitiesManifestV1.ids, {}, {});
        setOverviewDescriptionVisible(true);
    };
    const onReadMoreClose = () => {
        setOverviewDescriptionVisible(false);
    };

    return (
        <Box data-element-name={OVERVIEW_ELEMENT_NAME} data-testid={OVERVIEW_ELEMENT_NAME}>
            <Divider color="base.subtle" mt="8" mb="12" />
            {isBotOrIsPrerender ? (
                <Box mb="12">
                    <div ref={ref}>
                        <ActivityDescription contentLocale={contentLocale} activityDescription={activity.description} />
                    </div>
                </Box>
            ) : (
                <Box maxHeight={`${maxHeight}px`} overflow="hidden" mb="12">
                    <BlurLine height={isExceedHeight ? `${maxHeight}px` : 'none'} blur={isExceedHeight ? 'linear-gradient(to bottom, black 70%, rgba(0, 0, 0, 0) 100%)' : 'none'}>
                        <div ref={ref}>
                            <ActivityDescription contentLocale={contentLocale} activityDescription={activity.description} disableLinkFocus={true} />
                        </div>
                    </BlurLine>
                </Box>
            )}
            {!isDisplayFullContent && isExceedHeight && !isBotOrIsPrerender && (
                <>
                    <TextLink data-element-name="activities-read-more-button" label={cms[337259]} onClick={onReadMoreClick} textVariant="body-s" />
                    <OverviewDescription
                        visible={isOverviewDescriptionVisible}
                        onClose={onReadMoreClose}
                        activityDescription={<ActivityDescription contentLocale={contentLocale} activityDescription={activity.description} />}
                    />
                </>
            )}
        </Box>
    );
};
ActivityOverview.displayName = 'ActivityOverview';

export default connect(mapStateToProps)(ActivityOverview);
